import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { Location } from './types';
import { ConcurrencySettingsSummary, UpdateConcurrencySettingsPayload } from './concurrencyTypes';
import {
  createLocation,
  deleteLocation,
  getConcurrencySettings,
  getLocation,
  getLocations,
  updateConcurrencySettings,
  updateLocation
} from './api';

export const CONCURRENCY_SETTINGS_QUERY_KEY = ['concurrency_settings'];

export function useGetLocations(
  params?: any,
  options?: UseQueryOptions<{ data: Location[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: Location[]; meta: MetaApiResponse }>({
    queryKey: ['locations', params],
    queryFn: () => getLocations(params),
    ...options
  });
}

export function useGetConcurrencySettings(options?: { enabled?: boolean }) {
  return useQuery<ConcurrencySettingsSummary>({
    queryKey: CONCURRENCY_SETTINGS_QUERY_KEY,
    queryFn: getConcurrencySettings,
    enabled: options?.enabled ?? true
  });
}

export function useUpdateConcurrencySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateConcurrencySettingsPayload) => updateConcurrencySettings(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(CONCURRENCY_SETTINGS_QUERY_KEY, data);
      queryClient.invalidateQueries({ queryKey: ['locations'], refetchType: 'active' });
    }
  });
}

export function useGetLocation(params?: Record<string, any>, options?: UseQueryOptions<Location, Error>) {
  const { locationId } = useParams<{ locationId: string }>();

  return useQuery<Location>({
    queryKey: ['location', locationId, params],
    queryFn: () => getLocation(locationId!, params),
    enabled: Boolean(locationId),
    ...options
  });
}

export function useCreateOrUpdateLocation() {
  const navigate = useNavigate();
  const { locationId } = useParams<{ locationId: string }>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Location) => (locationId ? updateLocation(locationId!, payload) : createLocation(payload)),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['locations']
      });
      await queryClient.refetchQueries({
        queryKey: ['location']
      });
      navigate(-1);
    }
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['locations'],
        refetchType: 'active'
      });
    }
  });
}
